import React, {useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import {usePrevious} from 'src/hooks/usePrevious';
import {getEventInfo} from 'src/modules/event/services';
import {
    filterAddedAttributes,
} from 'src/modules/event-create/attributes-block/attribute-form/utils/filterAddedAttributes';
import {
    mergeDeprecatedAttributes,
} from 'src/modules/event-create/attributes-block/attribute-form/utils/mergeDeprecatedAttributes';
import {setAttributesDeprecated} from 'src/modules/event-create/attributes-block/attribute-form/utils/setAttributesDeprecated';
import {filterDecorationTemplate} from 'src/modules/event-create/attributes-block/utils/filterDecorationTemplate';
import {mapTemplatesToAttributes} from 'src/modules/event-create/attributes-block/utils/mapTemplatesToAttributes';
import {Params} from 'src/modules/event-create/event-create.controller';
import {sortAttributes} from 'src/modules/event-create/utils/sortAttributes';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppSelector} from 'src/store';
import {v4 as uuidv4} from 'uuid';

import {EventCreationMode} from '../constants';
import {CreationAttribute, UpdateChangeRequest, UpdateChangeRequestType} from '../typedefs';

import {attributesMapToSave} from './attribute-form/utils/attributesMapToSave';
import {AttributesBlockView} from './attributes-block.view';
import {DEFAULT_ATTRIBUTE_DETAIL} from './constants';

type Props = {
    attributes: CreationAttribute[],
    decorationTemplate: CreationAttribute[],
    template: CreationAttribute[],
    mode: EventCreationMode,
    changeRequestId: number | null,
    eventChangeRequest: EventChangeRequest,
    validationHighLight: boolean,
    isLoadingAttributes: boolean,
    isEventMarkedDeprecated: boolean,
    onUpdateChangeRequest: (value: UpdateChangeRequest, type: UpdateChangeRequestType) => void,
};

const MIN_ATTRIBUTES_TO_SCROLL = 2;

export const AttributesBlockController = (props: Props) => {
    const {
        attributes,
        mode,
        validationHighLight,
        decorationTemplate,
        template,
        changeRequestId,
        eventChangeRequest,
        isLoadingAttributes,
        isEventMarkedDeprecated,
        onUpdateChangeRequest,
    } = props;

    const eventId = parseInt(useParams<Params>().eventId || '');
    const eventCodeInfo = useAppSelector(getEventInfo)?.[eventId];
    const [savedTemplate, setSavedTemplate] = useState<CreationAttribute[]>([]);
    const [updateAttributes, setUpdateAttributes] = useState<CreationAttribute[]>([]);
    const [addedAttributes, setAddedAttributes] = useState<CreationAttribute[]>([]);
    const [isAllAttributesMarkedDeprecated, setIsAllAttributesMarkedDeprecated] = useState<boolean>(false);
    const prevAddedAttributes = usePrevious(addedAttributes);

    const createEmptyAttribute = (): CreationAttribute => ({
        ...DEFAULT_ATTRIBUTE_DETAIL,
        attributeId: uuidv4(),
        ...(attributes[0]?.eventId && {eventId: attributes[0].eventId}),
    });

    useEffect(() => {
        if (template.length && !savedTemplate.length) {
            setSavedTemplate(template);
        }
        if (mode === EventCreationMode.create) {
            onUpdateChangeRequest(
                attributesMapToSave([...template, ...addedAttributes]),
                UpdateChangeRequestType.ATTRIBUTES,
            );
            return;
        }
        onUpdateChangeRequest(
            attributesMapToSave([...updateAttributes, ...addedAttributes]),
            UpdateChangeRequestType.ATTRIBUTES,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, updateAttributes]);

    useEffect(() => {
        const filteredAddedAttribute = filterAddedAttributes(savedTemplate, template, addedAttributes);
        const resolveTemplate = filteredAddedAttribute.length !== addedAttributes.length ? [] : template;

        const hasEmptyAttribute = filteredAddedAttribute.some(attribute => attribute.id === undefined);

        setAddedAttributes([
            ...filteredAddedAttribute,
            ...(hasEmptyAttribute ? [] : [createEmptyAttribute()]),
        ]);

        if (template.length) {
            onUpdateChangeRequest(
                attributesMapToSave([...resolveTemplate, ...addedAttributes]),
                UpdateChangeRequestType.ATTRIBUTES,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [template, updateAttributes, savedTemplate]);

    useEffect(() => {
        onUpdateChangeRequest(attributesMapToSave(updateAttributes), UpdateChangeRequestType.ATTRIBUTES);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setAddedAttributes([...attributes
            .filter(attribute => !attribute.id)
            .map(attr => ({...attr, attributeId: uuidv4()}))]);
        setUpdateAttributes(sortAttributes([...attributes.filter(attribute => attribute.id)]));
    }, [mode, attributes]);

    useEffect(() => {
        const allAttributes = [...attributes, ...addedAttributes, ...template];
        const filteredDecorationTemplate = filterDecorationTemplate(decorationTemplate, allAttributes);

        if (!isEmpty(filteredDecorationTemplate)) {
            const attributesToAdd = mapTemplatesToAttributes(filteredDecorationTemplate, attributes);

            setAddedAttributes([...attributesToAdd, ...addedAttributes]);

            onUpdateChangeRequest(
                attributesMapToSave([...template, ...addedAttributes, ...updateAttributes, ...attributesToAdd]),
                UpdateChangeRequestType.ATTRIBUTES,
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [decorationTemplate]);

    useEffect(() => {
        if (isEventMarkedDeprecated) {
            setUpdateAttributes(prev => setAttributesDeprecated(prev, true));
            setAddedAttributes(prev => setAttributesDeprecated(prev, true));
            setIsAllAttributesMarkedDeprecated(true);
            return;
        }

        if (isAllAttributesMarkedDeprecated) {
            setAddedAttributes(prev => setAttributesDeprecated(prev, false));
            setUpdateAttributes(prev => mergeDeprecatedAttributes(prev, eventCodeInfo.attributes));
            setIsAllAttributesMarkedDeprecated(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllAttributesMarkedDeprecated, isEventMarkedDeprecated]);

    const onAdd = () => {
        const currentAttributes = [...addedAttributes, createEmptyAttribute()];

        setAddedAttributes(currentAttributes);
        onUpdateChangeRequest(
            attributesMapToSave([...template, ...updateAttributes, ...currentAttributes]),
            UpdateChangeRequestType.ATTRIBUTES,
        );
    };

    const onRemove = (addedAttribute: CreationAttribute) => {
        const filteredAddedAttributes = addedAttributes.filter(attribute =>
            attribute.attributeId !== addedAttribute.attributeId);
        setAddedAttributes(filteredAddedAttributes);
        onUpdateChangeRequest(
            attributesMapToSave([...template, ...updateAttributes, ...filteredAddedAttributes]),
            UpdateChangeRequestType.ATTRIBUTES,
        );
    };

    const onChangeAttribute = (changedAttribute: CreationAttribute) => {
        if (changedAttribute.attributeId) {
            const updatedAttributes = addedAttributes.map(attribute =>
                (changedAttribute.attributeId === attribute.attributeId ? changedAttribute : attribute));

            setAddedAttributes(updatedAttributes);
            onUpdateChangeRequest(
                attributesMapToSave([...template, ...updateAttributes, ...updatedAttributes]),
                UpdateChangeRequestType.ATTRIBUTES,
            );
            return;
        }

        if (changedAttribute.id) {
            const updatedAttributes = updateAttributes.map(attribute =>
                (changedAttribute.id === attribute.id ? changedAttribute : attribute));
            setUpdateAttributes(updatedAttributes);
            onUpdateChangeRequest(
                attributesMapToSave([...template, ...updatedAttributes, ...addedAttributes]),
                UpdateChangeRequestType.ATTRIBUTES,
            );
        }
    };

    useEffect(() => {
        const shouldScroll = addedAttributes.length >= MIN_ATTRIBUTES_TO_SCROLL
            && prevAddedAttributes.length < addedAttributes.length;

        if (shouldScroll) {
            window.scroll(0, document.body.scrollHeight);
        }
    }, [addedAttributes, prevAddedAttributes.length]);

    return (
        <AttributesBlockView {...{
            attributes: updateAttributes,
            template,
            mode,
            changeRequestId,
            eventChangeRequest,
            validationHighLight,
            addedAttributes,
            isLoadingAttributes,
            isEventMarkedDeprecated,
            onAdd,
            onRemove,
            onChangeAttribute,
        }}
        />
    );
};
